import {prepareForSave, prepareForUse} from "@/js/store/offline/data";
import {db} from "@/js/store/offline/database";
import {Directory, Encoding, Filesystem} from "@capacitor/filesystem";
import {config} from "@/config/config";
import {Zip} from "@capawesome-team/capacitor-zip";
import {Capacitor} from "@capacitor/core";
import {Share} from "@capacitor/share";

export async function getDocumentById(documentId) {
  if (!documentId) return null;
  return prepareForUse(await db.documents.get(documentId) || null)
}

export async function getDocumentTypeById(documentId) {
  const document = await getDocumentById(documentId);
  if (document?.documentCategoryId === 2) return 'project';
  if (document?.documentCategoryId === 4) return 'layer';
  return 'document';
}

export async function getDocumentPath(documentId, paths = []) {
  const type = await getDocumentTypeById(documentId);
  if (type === 'project') {
    const project = await getDocumentById(documentId);
    paths.unshift(project.title);
  } else if (type === 'layer') {
    const layer = await getDocumentById(documentId);
    if (layer.parentDocumentId && !paths.includes('Layers')) {
      paths.push('Layers');
    }
    paths.push(layer.title);
    if (layer.parentDocumentId) {
      paths.unshift(await getDocumentPath(layer.parentDocumentId, []));
    }
  }
  return paths.join('/');
}

export async function saveDocumentJson(documentId) {
  let document = await getDocumentById(documentId);

  if (document?.markers?.length) {
    for (let marker of document?.markers) {
      const newMarker = marker.split('/').pop();
      document = findAndReplaceInJson(document, marker, newMarker);
      document = findAndReplaceInJson(document, marker.replace(/\/\d+/, ""), newMarker);
    }
  }

  const documentPath = `${config.APP_MOBILE_FOLDER}/${await getDocumentPath(documentId)}/${document.title}.json`;
  await Filesystem.writeFile({
    path: decodeURIComponent(documentPath),
    data: JSON.stringify(document),
    directory: Directory.Documents,
    recursive: true,
    encoding: Encoding.UTF8,
  });
  const localFile = await Filesystem.getUri({
    path: decodeURIComponent(documentPath),
    directory: Directory.Documents,
  });
  return decodeURIComponent(localFile.uri);
}

export async function getLayerGeometries(documentId) {
  let features = [];
  const result = await db.geometries.filter(el => el.documentId === documentId).toArray()
  if (result) {
    features = prepareForUse(result).map(x => ({
      ...x,
      ...x.geometry,
      id: x.geometryId
    }));
  }
  return {
    "type": "FeatureCollection",
    "features": features
  }
}

export async function saveLayerGeojson(documentId) {
  const document = await getDocumentById(documentId);
  const documentPath = `${config.APP_MOBILE_FOLDER}/${await getDocumentPath(documentId)}/${document.title}.geojson`;
  let geometries = await getLayerGeometries(documentId);
  if (geometries.features.length > 0) {
    for (const geometry of geometries.features) {
      for (let key in geometry.properties) {
        if (/capacitor/.test(geometry.properties[key])) {
          geometry.properties[key] = geometry.properties[key].split('/').pop();
        }
      }
    }
  }
  await Filesystem.writeFile({
    path: decodeURIComponent(documentPath),
    data: JSON.stringify(geometries),
    directory: Directory.Documents,
    recursive: true,
    encoding: Encoding.UTF8,
  });
  const localFile = await Filesystem.getUri({
    path: decodeURIComponent(documentPath),
    directory: Directory.Documents,
  });
  return decodeURIComponent(localFile.uri);
}

export async function prepareExportFiles(documentId) {
  const documentType = await getDocumentTypeById(documentId);
  const documentData = await getDocumentById(documentId);
  await saveDocumentJson(documentId);

  if (documentType === 'layer') {
    await saveLayerGeojson(documentId);
  }

  if (documentType === 'project') {
    let children = await db.documents.filter(child => child.parentDocumentId === documentId).toArray()
    for (const child of children) {
      await prepareExportFiles(child.documentId);
    }
  }
}

export async function getDocumentZip(documentId) {
  const documentData = await getDocumentById(documentId);

  await checkStoragePermissions();

  await prepareExportFiles(documentId);

  const documentFolder = await Filesystem.getUri({
    path: `${config.APP_MOBILE_FOLDER}/${await getDocumentPath(documentId)}`,
    directory: Directory.Documents,
  });

  const documentArchive = await Filesystem.getUri({
    path: `${config.APP_MOBILE_FOLDER}/${documentData.title}.zip`,
    directory: Directory.Documents,
  });

  await Zip.zip({
    source: decodeURIComponent(documentFolder.uri),
    destination: decodeURIComponent(documentArchive.uri),
  });

  return {
    uri: decodeURIComponent(documentArchive.uri),
    path: `${config.APP_MOBILE_FOLDER}/${decodeURIComponent(documentData.title)}.zip`,
    directory: Directory.Documents,
  };
}

export function getFilenameWithoutExtension(filePath) {
  const fileName = filePath.split('/').pop(); // Get the last part after '/'
  return (fileName.substring(0, fileName.lastIndexOf('.')) || fileName);
}

export async function importDocumentFromFolder(folderPath, params = {}, parent = undefined) {
  console.log({folderPath, params, parent});
  await checkStoragePermissions();
  const folder = await Filesystem.readdir({
    path: folderPath
  })
  if (folder.files) {
    let documentId;
    let attachmentsFolder;
    let markersFolder;
    const sortedFiles = folder.files.sort((a, b) => {
      // Place folders at the end
      if (a.type === 'directory' && b.type === 'file') return 1;
      if (a.type === 'file' && b.type === 'directory') return -1;
      // If both are files or both are folders, sort by name descending
      return b.name.localeCompare(a.name);
    });

    for (const file of sortedFiles) {
      if (file.type === 'file' && /json$/.test(file.uri)) {
        const documentData = await Filesystem.readFile({
          path: file.uri,
          encoding: Encoding.UTF8,
        });

        if (/\.json/.test(file.uri)) {
          const document = JSON.parse(documentData.data.toString());
          // save document
          delete document.documentId;
          delete document.children;
          delete document.entityId;
          delete document.authorId;
          document.organizationId = params.organizationId || config.APP_ORGANIZATION_ID;
          document.parentDocumentId = parent || params.parentDocumentId;
          if (document?.properties?.dataSource?.isTiles) {
            document.properties.dataSource.isTiles = false
          }

          parent = documentId = await db.documents.add({
            ...prepareForSave(document),
            documentTypeId: document?.documentType?.documentTypeId || document?.documentTypeId || 40,
            documentCategoryId: document?.documentCategory?.documentCategoryId || document?.documentCategoryId,
          }).catch((error) => {
          });

          attachmentsFolder = await Filesystem.getUri({
            path: `${config.APP_MOBILE_FOLDER}/${await getDocumentPath(documentId)}/Attachments`,
            directory: Directory.Documents,
          });

          markersFolder = await Filesystem.getUri({
            path: `${config.APP_MOBILE_FOLDER}/${await getDocumentPath(documentId)}/Markers`,
            directory: Directory.Documents,
          });

          if (document?.markers?.length) {
            let document = await getDocumentById(documentId);
            for (let marker of document?.markers) {
              const newMarker = decodeURIComponent(Capacitor.convertFileSrc(markersFolder.uri) + '/' + marker.split('/').pop());
              document = findAndReplaceInJson(document, marker, newMarker);
              document = findAndReplaceInJson(document, marker.replace(/\/\d+/, ""), newMarker);
            }
            console.log({document})
            await db.documents.update(documentId, {
              ...prepareForSave(document)
            }).catch((error) => {
            });
          }

        }

        if (/\.geojson/.test(file.uri)) {
          const document = JSON.parse(documentData.data.toString());
          // save geometries
          if (document?.features) {
            let features = []
            for (let geometry of document.features) {
              delete geometry.geometryId;
              delete geometry.id;
              geometry.documentId = documentId;
              for (let key in geometry.properties) {
                if (/(.jpg|.jpeg|.png|.gif|.pdf|.doc|.txt|.docx|.ppt|.odt)$/.test(geometry.properties[key])) {
                  geometry.properties[key] = decodeURIComponent(Capacitor.convertFileSrc(attachmentsFolder.uri) + '/' + geometry.properties[key].split('/').pop());
                }
              }
              features.push({
                documentId: documentId,
                geometry: geometry
              });
            }
            await db.geometries.bulkPut(prepareForSave(features), {allKeys: true})
          }
        }
      }

      if (file.type === 'directory') {
        await importDocumentFromFolder(file.uri, params, parent);
      }
    }
  }
  return parent;
}

export async function deleteDocumentFolder(documentId) {
  try {
    await Filesystem.rmdir({
      path: decodeURIComponent(`${config.APP_MOBILE_FOLDER}/${await getDocumentPath(documentId)}`),
      directory: Directory.Documents,
      recursive: true
    });
  } catch (e) {
    console.error(e);
  }
}

export async function renameDocumentFolder(from, to) {
  try {
    await Filesystem.rename({
      from: decodeURIComponent(`${config.APP_MOBILE_FOLDER}/${from}`),
      to: decodeURIComponent(`${config.APP_MOBILE_FOLDER}/${to}`),
      directory: Directory.Documents,
      toDirectory: Directory.Documents,
    });
    // rename direct files too
    const renamedFolder = await Filesystem.getUri({
      path: `${config.APP_MOBILE_FOLDER}/${to}`,
      directory: Directory.Documents,
    });
    const folder = await Filesystem.readdir({
      path: renamedFolder.uri
    })
    if (folder.files) {
      for (const file of folder.files) {
        if (/json$/.test(file.uri) && file.type === 'file') {
          const newFolderName = getFilenameWithoutExtension(decodeURIComponent(renamedFolder.uri));
          const fileExtension = file.name.split('.').pop();
          await Filesystem.rename({
            from: decodeURIComponent(`${config.APP_MOBILE_FOLDER}/${to}/${file.name}`),
            to: decodeURIComponent(`${config.APP_MOBILE_FOLDER}/${to}/${newFolderName}.${fileExtension}`),
            directory: Directory.Documents,
            toDirectory: Directory.Documents,
          });
        }
      }
    }
  } catch (e) {
    console.error(e);
  }
}

export async function imageToBase64(url) {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

export async function downloadBlob(blob, filename) {
  await checkStoragePermissions();

  await Filesystem.writeFile({
    path: `${config.APP_MOBILE_FOLDER}/temp/${filename + '.zip'}`,
    data: blob,
    directory: Directory.Documents,
    recursive: true,
  });

  const downloadFile = await Filesystem.getUri({
    path: `${config.APP_MOBILE_FOLDER}/temp/${filename + '.zip'}`,
    directory: Directory.Documents,
  });

  await Share.share({
    files: [downloadFile.uri]
  }).catch(error => {
    console.log(error);
  });

  try {
    await Filesystem.deleteFile({
      path: `${config.APP_MOBILE_FOLDER}/temp/${filename + '.zip'}`,
      directory: Directory.Documents,
    });
  } catch (e) {
    console.log(e);
  }
}

export async function checkStoragePermissions() {
  if (Capacitor.getPlatform() !== 'android') return;

  const status = await Filesystem.checkPermissions();
  if (status.publicStorage !== 'granted') {
    const requestStatus = await Filesystem.requestPermissions();
    if (requestStatus.publicStorage !== 'granted') {
      console.warn('Storage permission not granted. Some legacy features might fail on older Android versions.');
    }
  }
}

export function findAndReplaceInJson(jsonObj, searchText, replaceText) {
  if (typeof jsonObj === "string") {
    // Replace if it's a string
    return jsonObj.includes(searchText) ? jsonObj.replace(searchText, replaceText) : jsonObj;
  } else if (Array.isArray(jsonObj)) {
    // Recursively handle arrays
    return jsonObj.map(item => findAndReplaceInJson(item, searchText, replaceText));
  } else if (typeof jsonObj === "object" && jsonObj !== null) {
    // Recursively handle objects
    const newObj = {};
    for (const key in jsonObj) {
      newObj[key] = findAndReplaceInJson(jsonObj[key], searchText, replaceText);
    }
    return newObj;
  } else {
    // Return as is for other data types (number, null, etc.)
    return jsonObj;
  }
}
